/**
 * esign.ts — the constants and small helpers every signing surface in this app shares.
 *
 * Three things live here that would otherwise be copied into four files and drift:
 *
 *   1. THE KILL SWITCH. `VITE_SIGNING_DISABLED` hides every signing CTA and closes the
 *      `/sign` route. It is read once, at module load, because a flag that can change
 *      mid-session is a flag that can strand a half-finished ceremony.
 *   2. THE STORAGE PATH. `signatures/<scope>/<document_id>/<PARTY>-<uuid>.png`, where the
 *      SCOPE IS THE SERVER'S ANSWER and never one worked out here. It is fixed by SQL at
 *      two independent points — the bucket's INSERT policy (`can_write_signature_object`)
 *      and `agreement_sign`, which anchors the same shape and additionally requires
 *      segment 3 to be THIS document. Building it anywhere but here would mean two places
 *      to keep in step, and a path SQL refuses leaves an orphan PNG in the bucket.
 *   3. THE ERROR VOCABULARY. Six raised codes mean something specific to a signer and
 *      change what they should do next; everything else gets one calm sentence. See
 *      `signingErrorMessage`.
 *
 * ── THIS IS fleet-driver's MODULE, NARROWED TO ONE SEAT AND ONE INSTRUMENT ────
 * The driver app's copy carries a witness predicate, a legacy-paper arc index and a
 * unified-list builder over two generations of the same record. None of the three has a
 * subject here: this app shows ONE doc type, PARTNER_MANAGEMENT, whose roster is PARTNER
 * then COMPANY_REP, and paper agreements are retired [D-B49] — there is no scanned twin for
 * an arc key to deduplicate against. What is left is the half that is genuinely shared, and
 * it is kept spelled the way the driver spells it so the two can be diffed.
 */

/** Environment flag: `true` closes the signing route and hides every CTA. */
export const SIGNING_DISABLED =
    String(import.meta.env.VITE_SIGNING_DISABLED ?? '').trim().toLowerCase() === 'true';

/**
 * The consent wording this app shows, versioned. Stored on every signature row as
 * `consent_text_version`, so a later change to the attestation text is provable against
 * what a given signer actually agreed to. BUMP THIS whenever the attestation copy changes
 * in substance.
 *
 * ── v1, 2026-09-09: THE PARTNER'S OWN NAMESPACE, AND IT STARTS AT ONE ─────────
 * The driver app is at `driver-app-consent-v5` and this is NOT v5 of the same thing. The
 * namespace is per-app because the attested substance is per-app: a partner ticking this
 * checklist confirms the All-In Cost they stated, the share of each Weekly Payment they
 * receive, the Management Fee that is kept out of it, and — in the words of the Schedule's
 * own "What the Partner states to be true" — that everything below the All-In Cost is an
 * EXPECTATION AND NOT A PROMISE. No sentence a driver ticks says any of that, and no
 * sentence a partner ticks mentions a hire-purchase price they owe or an advance made to
 * them. Sharing a version line between the two would make every stored row ambiguous about
 * which set of words it stands for, which is the one thing this column exists to prevent.
 *
 * There is nothing to be backwards-compatible with: this app has never taken a signature,
 * so v1 is the first wording any `agreement_signatures.consent_text_version` row in the
 * PARTNER seat will ever carry.
 *
 * ── v2, 2026-09-23: THE CASH PRICE, AND A MAXIMUM DURATION [D-B59] ────────────
 * Vehicle Schedule v3 states the Cash price where the Schedule before it stated the All-In
 * Cost, works every figure on the whole Hire-purchase price, and states the duration as a
 * maximum. The checklist follows it in substance: the partner states the Cash price is what
 * they have put into the vehicle, confirms the arrangement runs AT MOST the weeks shown, and
 * ticks no sentence about the Driver's Deposit, which the v3 Schedule does not name; the
 * advance statement stands on its own. A row carrying v1 was ticked against the old
 * sentences, and a row carrying v2 against these.
 *
 * A BUMP DOES NOT REACH BACKWARDS, which is the only reason the column exists.
 * `agreement_signatures.consent_text_version` is plain nullable text with no check
 * constraint and no foreign key: a stored row keeps the string it was signed under for
 * good, and only signatures taken after this line changes carry the new one. It does reach
 * a ceremony still in progress: `loadDraft` discards a draft saved under another version.
 */
export const CONSENT_TEXT_VERSION = 'partner-app-consent-v2';

/** The bucket the ceremony uploads its drawn signature into. */
export const SIGNATURE_BUCKET = 'agreement-signatures';

/** localStorage namespace, per the app conventions. */
export const ESIGN_STORAGE_PREFIX = 'esign:';

// ─── Labels ──────────────────────────────────────────────────────────────────

/**
 * `agreement_templates.title` FOR EACH PUBLISHED TYPE, VERBATIM — not this app's own words
 * for it. The document card titles a record from this map and the pending-signature banner
 * names the next document from it, while the partner is looking at a page whose own title
 * carries the registry's string; a second name for the same instrument reads as a second
 * instrument.
 *
 * ONE ENTRY, because one doc type is all a partner session can address. The string is
 * quoted from the published template — `partner_vehicle_schedule` v1 is registered with
 * title 'Hire Fleet Management Agreement'
 * (fleet-backend `20260908020000_no_driver_signs_before_the_vehicles_partner_has.sql:889`)
 * and that is the heading printed at the top of the body the partner reads.
 *
 * NOTE the driver app's map calls this same type "Partner Management Agreement", a
 * descriptive label it invented when the type had no published template of its own. It has
 * one now, and this is its name.
 *
 * IT IS THE FALLBACK AND NOT THE ANSWER — see `documentLabel`, which is what the surfaces
 * call.
 */
export const DOC_TYPE_LABELS: Record<string, string> = {
    PARTNER_MANAGEMENT: 'Hire Fleet Management Agreement',
};

/**
 * What to call a document on a card: ITS OWN TITLE, with the map above as the fallback.
 *
 * THE SERVER ALREADY SENDS THE ANSWER. `agreement_render`, `get_my_partner_agreements` and
 * `get_my_pending_signatures` all project `title`, and it is the TEMPLATE's own — the string
 * printed on the title page the partner has open. The map stays behind it for a row that
 * arrives without one.
 */
export function documentLabel(title: string | null | undefined, docType: string): string {
    const named = typeof title === 'string' ? title.trim() : '';
    if (named.length > 0) return named;
    return DOC_TYPE_LABELS[docType] ?? 'Agreement';
}

/**
 * The two seats on a Vehicle Schedule. `PARTNER` is "You" here for the same reason `DRIVER`
 * is "You" in the driver app: the roster is rendered to the person holding that seat.
 */
export const PARTY_LABELS: Record<string, string> = {
    PARTNER: 'You',
    COMPANY_REP: 'Primelane',
};

/** How a party chip reads on a card. `role_label` from the template wins when present. */
export function partyLabel(party: string, roleLabel?: string | null): string {
    return PARTY_LABELS[party] ?? roleLabel ?? party;
}

/**
 * The parties a partner session can ever be asked to sign as.
 *
 * ONE, and the narrowness is the correctness. `agreement_sign`'s PARTNER arm binds
 * `my_partner_id()` to the vehicle's current partner AND to the partner the referent
 * records (fleet-backend `20260908020000:1857-1876`); no other seat on any document is
 * reachable from a partner session at all. A wider list here would put a "sign this" button
 * on a seat the server answers `Access denied` to.
 */
export const MY_PARTIES = ['PARTNER'] as const;

export function isMyParty(party: string | null | undefined): boolean {
    return party === 'PARTNER';
}

// ─── "Is this one waiting for me?" ───────────────────────────────────────────

/** Document statuses in which a signature can still be taken. */
export const OPEN_DOCUMENT_STATUSES = ['PENDING_REVIEW', 'IN_SIGNING'];

/** Signature-row statuses that mean the party has not signed yet. */
export const AWAITING_SIGNATURE_STATUSES = ['PENDING', 'SENT', 'VIEWED'];

/** Structural shape of one roster-carrying row, as this predicate reads it. */
export interface SignableDocument {
    status: string;
    signatures: readonly { party: string; status: string | null }[] | null;
}

/**
 * Whether THIS partner still owes THIS document a signature.
 *
 * One definition, because two of them drift: the agreements card decides whether to offer
 * "Review & Sign" with it, and the pending banner counts with it. A badge that counted a
 * different set from the cards underneath it would send a partner looking for work that is
 * not there.
 */
export function awaitingMySignature(document: SignableDocument): boolean {
    if (SIGNING_DISABLED) return false;
    if (!OPEN_DOCUMENT_STATUSES.includes(document.status)) return false;
    const mine = (document.signatures ?? []).find((row) => isMyParty(row.party));
    return !!mine && AWAITING_SIGNATURE_STATUSES.includes(mine.status ?? '');
}

/**
 * The same question asked of `get_my_partner_agreements`, which projects the caller's OWN
 * seat as a scalar rather than a roster.
 *
 * The RPC does that deliberately — "the company's countersignature is a fact about the
 * document's status, which is the column beside this one; what a partner needs to know is
 * whether THEY have signed" (fleet-backend `20260908010000:4951-4954`) — so the roster
 * shape `awaitingMySignature` wants does not exist on a list row. Rather than fabricate a
 * one-element roster at each call site, the scalar gets its own spelling of the identical
 * rule, and the two are pinned together by a test.
 */
export function awaitingMySignatureOnRow(row: {
    status: string;
    signature_status: string | null;
}): boolean {
    if (SIGNING_DISABLED) return false;
    if (!OPEN_DOCUMENT_STATUSES.includes(row.status)) return false;
    return AWAITING_SIGNATURE_STATUSES.includes(row.signature_status ?? '');
}

/** What "can this be opened in the at-rest viewer?" reads. */
export interface ViewableDocumentSummary {
    status: string;
    finalized_at: string | null;
}

/**
 * Whether a card may offer "View agreement" — the route to `/agreement/:documentId`.
 *
 * FINALIZED, and nothing else. The driver app carries a second clause here for the witness
 * agreement, which becomes viewable the moment its witness signs and before it is executed;
 * there is no such instrument in this app. Before a Vehicle Schedule is finalized the
 * partner's own ceremony is where the text lives, and the card routes there instead.
 */
export function agreementViewable(document: ViewableDocumentSummary): boolean {
    if (document.status === 'VOIDED' || document.status === 'SUPERSEDED') return false;
    return !!document.finalized_at;
}

// ─── "Why is there no button on this card?" ──────────────────────────────────

/**
 * The sentence a card shows when it offers no action at all.
 *
 * HERE RATHER THAN BESIDE THE CARD because it is a classification, in the same family as
 * `awaitingMySignature` — the two decide between them what a card says, and a sentence that
 * claims the wrong thing renders perfectly.
 *
 * THE DRIVER'S WITNESS BRANCH IS ABSENT because its subject is: that branch exists for a
 * card the driver can see but holds no seat on, and every document on this page seats the
 * partner. The three sentences left are true of every state a Vehicle Schedule reaches.
 */
export function inactionReason(document: { status: string }): string {
    if (document.status === 'VOIDED') return 'Primelane withdrew this document.';
    if (document.status === 'SUPERSEDED') return 'Primelane replaced this with a newer version.';
    if (SIGNING_DISABLED) return 'Signing is temporarily unavailable.';
    if (document.status === 'COMPLETED') return 'Both parties have signed this agreement.';
    return 'Primelane is still preparing this document.';
}

// ─── "To sign" versus "already dealt with" ───────────────────────────────────

/**
 * Whether a document can still move — it is being prepared, or it is being signed.
 *
 * DELIBERATELY NOT `awaitingMySignature`. That predicate answers "is there work for ME",
 * and it is false while the partner waits on Primelane's countersignature or while the kill
 * switch is on. A document in either of those states is still a live one, and grouping by
 * the narrower predicate would drop it out of the to-sign list the moment the partner signed
 * — into a heading that says the thing is finished, which it is not.
 */
function isLiveDocument(document: { status: string }): boolean {
    return OPEN_DOCUMENT_STATUSES.includes(document.status);
}

/** The columns the grouping reads. A `get_my_partner_agreements` row satisfies it. */
export interface GroupableDocument {
    status: string;
    created_at: string;
    finalized_at: string | null;
}

export interface AgreementDocumentGroups<T> {
    /** Still in flight: "Agreements to sign". */
    live: T[];
    /** Finished or abandoned: "Signed & past agreements". Newest first. */
    past: T[];
}

/** An unparseable or absent timestamp sorts last rather than throwing the whole order out. */
function parseTime(value: string | null | undefined): number {
    const parsed = Date.parse(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Newest first, by whichever timestamp the document actually has.
 *
 * `finalized_at` RATHER THAN THE DRIVER'S `completed_at`, because that is the column this
 * app's roster projects — `get_my_partner_agreements` returns `created_at` and
 * `finalized_at` and no third date (fleet-backend `20260908010000:4928`).
 */
function documentTime(document: GroupableDocument): number {
    return parseTime(document.finalized_at ?? document.created_at);
}

/**
 * Splits the agreements list into the two things a partner reads it for.
 *
 * THE ASYMMETRY IS THE POINT. `live` is an ALLOWLIST — only the two statuses in which a
 * signature can still be taken may sit under a heading that asks for one, so a status this
 * app has not met yet can never fake an action. `past` is then EVERYTHING ELSE, not a second
 * allowlist of COMPLETED and VOIDED: a document must never disappear from a partner's page
 * because a status was added upstream, and SUPERSEDED already exists to prove the case.
 */
export function groupAgreementDocuments<T extends GroupableDocument>(
    documents: readonly T[] | null | undefined,
): AgreementDocumentGroups<T> {
    const live: T[] = [];
    const past: T[] = [];

    for (const document of documents ?? []) {
        if (isLiveDocument(document)) live.push(document);
        else past.push(document);
    }

    past.sort((a, b) => documentTime(b) - documentTime(a));

    // `live` keeps the server's order — `get_my_partner_agreements` already returns
    // `created_at DESC, id DESC`, so the newest thing asked of the partner is at the top and a
    // re-sort here would only be a second opinion to keep in step.
    return { live, past };
}

// ─── Storage path ────────────────────────────────────────────────────────────

/**
 * `<contract_id>` or `deposit/<contract_deposit_id>` — and nothing else may enter a path.
 *
 * ── IT IS THE DRIVER'S REGEX, UNCHANGED, AND THE DEPOSIT ARM IS NOT DEAD CODE ─
 * A Vehicle Schedule stamps `contract_id`, so `agreement_path_scope` answers a bare contract
 * id for every document this app will ever see and the `deposit/` alternative never matches
 * here. It stays because this pattern is a re-check of A STRING SQL PRODUCES, and the SQL
 * that produces it is one function shared by every arc in the subsystem: narrowing the
 * client's copy to the shape today's documents happen to take would make this app the only
 * one that rejects a scope its own database considers valid. The string comes from the
 * database and the database is trusted; it is re-checked because it is INTERPOLATED INTO AN
 * OBJECT NAME, and a builder that would splice `undefined` or a traversal into a bucket key
 * the day something upstream changes is not worth two lines.
 */
const SCOPE_SHAPE = /^(?:deposit\/)?[0-9]+$/;

/**
 * `signatures/<scope>/<document_id>/<PARTY>-<uuid>.png`, byte-compatible with the anchored
 * regexes in `can_write_signature_object` and `agreement_sign`. The party name is stripped
 * to the characters those regexes accept.
 *
 * ── THE SCOPE IS THE SERVER'S, AND THAT IS THE WHOLE POINT ────────────────────
 * `path_scope` arrives on `agreement_render` and `get_my_partner_agreements`, derived by
 * `agreement_path_scope` — the same derivation, so that "the ceremony composes its signature
 * upload path from one derivation rather than two"
 * (fleet-backend `20260908010000:4965-4966`). Recomposing it here from the contract this
 * document names would be a SECOND rule to keep in step with SQL's, and the two disagree the
 * moment SQL's does anything but return the contract id.
 *
 * NULL IS AN ANSWER, NOT AN ERROR TO ROUTE AROUND. Refusing BEFORE the upload is the
 * difference between an early rejection and an orphan PNG at a path `agreement_sign` will
 * then refuse — in a private bucket the signer cannot delete from.
 */
export function signatureObjectPath(
    pathScope: string | null | undefined,
    documentId: number,
    party: string,
    uuid: string,
): string | null {
    if (typeof pathScope !== 'string' || !SCOPE_SHAPE.test(pathScope)) return null;
    const safeParty = String(party).replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'PARTY';
    return `signatures/${pathScope}/${documentId}/${safeParty}-${uuid}.png`;
}

/** `crypto.randomUUID` is not in every mobile browser this app has to serve. */
export function randomUuid(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const rand = (Math.random() * 16) | 0;
        const value = ch === 'x' ? rand : (rand & 0x3) | 0x8;
        return value.toString(16);
    });
}

// ─── Hashing ─────────────────────────────────────────────────────────────────

/**
 * SHA-256 of a string as lowercase hex — the same digest SQL computes over
 * `rendered_md`, used ONLY as a cross-check that the bytes on screen are the bytes the
 * server hashed. The hash submitted with a signature is always the server's own
 * `content_sha256`, never this one.
 *
 * Returns null where `crypto.subtle` is unavailable (an insecure context), because a
 * missing cross-check must not become a reason a partner cannot sign.
 */
export async function sha256Hex(value: string): Promise<string | null> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    try {
        const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
        return Array.from(new Uint8Array(digest))
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return null;
    }
}

// ─── Images ──────────────────────────────────────────────────────────────────

/** `data:image/png;base64,…` -> the bytes the bucket wants. */
export function dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mime = /data:([^;]+)/.exec(header)?.[1] ?? 'image/png';
    const binary = atob(base64 ?? '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

// ─── The error vocabulary ────────────────────────────────────────────────────

/**
 * The raised codes a signer is entitled to see as themselves.
 *
 * SQL raises these only AFTER the caller has proven they may sign, so surfacing them
 * leaks nothing — and withholding them is what actually harms: a partner whose tab went
 * stale would be told "something went wrong" and would retry the same stale hash forever.
 *
 * ── THE LIST IS THE DRIVER'S, AND TWO OF THEM ARE HELD FOR A REASON ──────────
 * `agreement_sign`'s PARTNER arm raises neither AGREEMENT_PRIOR_SIGNATURE_REQUIRED nor
 * AGREEMENT_SIGN_ORDER today: the arm's only refusal is the three-limb `Access denied`
 * (fleet-backend `20260908020000:1857-1876`), and the Vehicle Schedule's roster puts PARTNER
 * at `sign_order` 1 with COMPANY_REP behind, so there is nobody the partner can be made to
 * wait for. Both stay because the alternative is worse in exactly one direction: an
 * unreachable branch costs a sentence nobody reads, while a code dropped from the list and
 * later raised reaches a signer as SIGNING_GENERIC_MESSAGE — "please try again" over a
 * refusal no retry can move. The sequencing rule this family exists to impose runs the OTHER
 * way (the partner signs first and the DRIVER waits on them, `20260908020000:1691`), so if a
 * gate ever lands on this arm it will be one of these two shapes.
 */
export const PASSTHROUGH_CODES = [
    'AGREEMENT_CONTENT_CHANGED',
    'AGREEMENT_TYPED_NAME_REQUIRED',
    'AGREEMENT_SIGN_ORDER',
    'AGREEMENT_CONTRACT_ACTIVE',
    'AGREEMENT_PRIOR_SIGNATURE_REQUIRED',
    'AGREEMENT_INSTRUMENT_MISMATCH',
] as const;

export type PassthroughCode = (typeof PASSTHROUGH_CODES)[number];

/** What each code means to somebody holding a phone, rather than to a database. */
const CODE_MESSAGES: Record<PassthroughCode, string> = {
    AGREEMENT_CONTENT_CHANGED:
        'This document changed since you opened it. Reload and read it again before signing.',
    AGREEMENT_TYPED_NAME_REQUIRED:
        'Type your full name in the box before submitting your signature.',
    AGREEMENT_SIGN_ORDER:
        'Another party has to sign before you. You will be notified when it is your turn.',
    AGREEMENT_CONTRACT_ACTIVE:
        'This contract is already active, so its agreements can no longer be changed.',
    // THE DRIVER'S SENTENCE NAMES THE DRIVER'S HIRE PURCHASE AGREEMENT, which is a document a
    // partner has no seat on, cannot open and is not waiting for. What this app can say
    // honestly is that the schedule is sequenced behind something and that the something is
    // not the partner's to do — so it ends on who acts next, rather than inviting a retry into
    // a call that has already answered.
    AGREEMENT_PRIOR_SIGNATURE_REQUIRED:
        'Another document has to be signed before this one. Primelane will let you know when this schedule is ready for your signature.',
    // INSTRUMENT-NEUTRAL, unlike the driver's, whose sentence is about an insurance year this
    // app has no concept of. It is the true sentence for the case that WOULD reach a partner:
    // a schedule sealed at one fee stops matching a contract that now states another, and the
    // remedy SQL itself names is withdraw-and-reissue rather than an edit underneath the
    // signatures (fleet-backend `20260908010000:5228`, `:5304`).
    AGREEMENT_INSTRUMENT_MISMATCH:
        'This document no longer matches the arrangement it was written for. Primelane will withdraw it and issue a corrected one, which appears here for you to sign.',
};

/** The one sentence every other failure gets. */
export const SIGNING_GENERIC_MESSAGE =
    'We could not record your signature. Please try again, or contact Primelane if it keeps happening.';

/** `CODE: sentence` — Postgres puts the code first, so the match is anchored, not a scan. */
const RAISED_CODE = /^([A-Z][A-Z0-9_]{2,63}):\s*/;

export interface SigningErrorInfo {
    code: PassthroughCode | null;
    message: string;
}

/**
 * Turns anything a signing call can reject with into `{ code, message }`.
 *
 * `code` is non-null only for the six passthrough codes, which is what lets a caller
 * react — offer a reload for a changed hash, focus the name field for a missing typed
 * name — instead of only printing a sentence.
 */
export function signingError(error: unknown): SigningErrorInfo {
    const raw =
        typeof error === 'string'
            ? error
            : ((error as { message?: string } | null)?.message ?? '');

    const explicit = (error as { code?: string } | null)?.code;
    const matched = RAISED_CODE.exec(raw.trim())?.[1] ?? explicit;

    const code = (PASSTHROUGH_CODES as readonly string[]).includes(matched ?? '')
        ? (matched as PassthroughCode)
        : null;

    return { code, message: code ? CODE_MESSAGES[code] : SIGNING_GENERIC_MESSAGE };
}

/** Convenience for a toast. */
export function signingErrorMessage(error: unknown): string {
    return signingError(error).message;
}

// ─── Draft resilience ────────────────────────────────────────────────────────

/**
 * What survives a reload mid-ceremony.
 *
 * THE DRAWN SIGNATURE IS DELIBERATELY ABSENT. Progress is worth persisting; a biometric
 * artefact sitting in localStorage after the tab closes is not. A restored draft that had
 * reached the signing step therefore lands back on the signing step with an empty pad.
 *
 * `contentSha256` is stored so a draft is discarded the moment the document underneath it
 * changes — resuming a checklist that was ticked against different text would be worse
 * than losing the progress. `consentTextVersion` does the same for the checklist's own
 * wording: the ticks are positional, so a statement reworded in place, with the document
 * and the statement count unchanged, would otherwise come back ticked unread.
 */
export interface CeremonyDraft {
    step: string;
    page: number;
    pagesSeen: number;
    readSeconds: number;
    terms: boolean[];
    typedName: string;
    contentSha256: string | null;
    /** The `CONSENT_TEXT_VERSION` the draft was saved under; `saveDraft` stamps it. */
    consentTextVersion: string;
    savedAt: number;
}

/** A draft older than this is stale enough that re-reading is the right answer. */
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

function draftKey(scope: string): string {
    return `${ESIGN_STORAGE_PREFIX}draft:${scope}`;
}

export function loadDraft(scope: string, contentSha256: string | null): CeremonyDraft | null {
    try {
        const raw = window.localStorage.getItem(draftKey(scope));
        if (!raw) return null;
        const draft = JSON.parse(raw) as CeremonyDraft;
        if (!draft || typeof draft !== 'object') return null;
        if (Date.now() - (draft.savedAt ?? 0) > DRAFT_TTL_MS) return null;
        // A draft ticked against different bytes is not this document's draft.
        if (contentSha256 && draft.contentSha256 && draft.contentSha256 !== contentSha256) {
            return null;
        }
        // Nor is one ticked against different consent wording, a draft from before the
        // version was stored included.
        if (draft.consentTextVersion !== CONSENT_TEXT_VERSION) return null;
        return draft;
    } catch {
        return null;
    }
}

export function saveDraft(
    scope: string,
    draft: Omit<CeremonyDraft, 'savedAt' | 'consentTextVersion'>,
): void {
    try {
        window.localStorage.setItem(
            draftKey(scope),
            JSON.stringify({
                ...draft,
                consentTextVersion: CONSENT_TEXT_VERSION,
                savedAt: Date.now(),
            } satisfies CeremonyDraft),
        );
    } catch {
        // A full or blocked localStorage costs progress, never the ceremony.
    }
}

export function clearDraft(scope: string): void {
    try {
        window.localStorage.removeItem(draftKey(scope));
    } catch {
        // Ignored for the same reason.
    }
}

// ─── The reader gate ─────────────────────────────────────────────────────────

/**
 * Whether a signer may leave the reader: they reached the last page AND spent the floor
 * of seconds on the step. Pure, and here rather than beside the reader component, because
 * the ceremony uses it to label its own primary button and applies it a second time to the
 * Terms booklet.
 */
export function readerGateOpen(
    pagesSeen: number,
    totalPages: number,
    readSeconds: number,
    secondsFloor: number,
): boolean {
    return pagesSeen >= totalPages && readSeconds >= secondsFloor;
}
